import ColorX from "../ColorX";

class ColorXAddOns {

  /**
   * cache if possible
   * @typedef {Object} CSStoFilterRetryOptions
   * @property {Number} perfect
   * @property {Number} maxLoss
   * @property {Number} maxRetry
   * 
   * @property {Object} CSStoFilterReturn
   * @property {Array<Number>} CSStoFilterReturn.values
   * @property {Number} CSStoFilterReturn.loss
   * @property {String} CSStoFilterReturn.filter
   * @property {Number} CSStoFilterReturn.trials
   * 
   * @param {String} css 
   * @param {CSStoFilterRetryOptions} options
   * @returns {CSStoFilterReturn}
   * 
   */
  static CSStoFilterRetry(css, {perfect = 0.2, maxLoss = 1, maxRetry = Infinity} = {}){
    let rtn = null;
    let count = 0;
    while(maxRetry > 0){
      count++;
      let _rtn = this.CSStoFilter(css);
      if(_rtn.loss > maxLoss){
        continue;
      }
      if(_rtn.loss <= perfect){
        rtn = _rtn;
        break;
      }

      maxRetry--;
      
      if(!rtn || _rtn.loss < rtn.loss){
        rtn = _rtn;
      }      
    }
    return {...rtn, trials: count};
  }

  /**
   * 
   * @param {String} css 
   */
  static CSStoFilter(css){
    let colorRGBA = ColorX.ExtractCSS(css);
    let colorHSL = ColorX.RGB2HSL(colorRGBA);
    let reusedColor = ColorX.RGBA(0, 0, 0, 1);

    let colorAll = {
      colorRGBA, colorHSL, reusedColor
    };

    let result = this.SolveNarrow(this.SolveWide(colorAll), colorAll);

    return {
      values: result.values,
      loss: result.loss,
      filter: this.CSS(result.values),
    };
  }

  static SolveWide(colorAll){
    const A = 5;
    const c = 15;
    const a = [60, 180, 18000, 600, 1.2, 1.2];

    let best = { loss: Infinity };
    for (let i = 0; best.loss > 25 && i < 10; i++) {
      const initial = [50, 20, 3750, 50, 100, 100];
      const result = this.SPSA(A, a, c, initial, 1000, colorAll);
      if (result.loss < best.loss) {
        best = result;
      }
    }
    return best;
  }

  static SolveNarrow(wide, colorAll) {
    const A = wide.loss;
    const c = 2;
    const A1 = A + 1;
    const a = [0.25 * A1, 0.25 * A1, A1, 0.25 * A1, 0.2 * A1, 0.2 * A1];
    return this.SPSA(A, a, c, wide.values, 500, colorAll);
  }

  static SPSA(A, a, c, values, iters, colorAll) {
    const alpha = 1;
    const gamma = 0.16666666666666666;

    let best = null;
    let bestLoss = Infinity;
    const deltas = new Array(6);
    const highArgs = new Array(6);
    const lowArgs = new Array(6);

    for (let k = 0; k < iters; k++) {
      const ck = c / Math.pow(k + 1, gamma);
      for (let i = 0; i < 6; i++) {
        deltas[i] = Math.random() > 0.5 ? 1 : -1;
        highArgs[i] = values[i] + ck * deltas[i];
        lowArgs[i] = values[i] - ck * deltas[i];
      }

      const lossDiff = this.Loss(highArgs, colorAll) - this.Loss(lowArgs, colorAll);
      for (let i = 0; i < 6; i++) {
        const g = lossDiff / (2 * ck) * deltas[i];
        const ak = a[i] / Math.pow(A + k + 1, alpha);
        values[i] = fix(values[i] - ak * g, i);
      }

      const loss = this.Loss(values, colorAll);
      if (loss < bestLoss) {
        best = values.slice(0);
        bestLoss = loss;
      }
    }
    return { values: best, loss: bestLoss };

    function fix(value, idx) {
      let max = 100;
      if (idx === 2 /* saturate */) {
        max = 7500;
      } else if (idx === 4 /* brightness */ || idx === 5 /* contrast */) {
        max = 200;
      }

      if (idx === 3 /* hue-rotate */) {
        if (value > max) {
          value %= max;
        } else if (value < 0) {
          value = max + value % max;
        }
      } else if (value < 0) {
        value = 0;
      } else if (value > max) {
        value = max;
      }
      return value;
    }
  }

  static Loss(filters, colorAll) {
    let {colorRGBA, colorHSL, reusedColor} = colorAll ?? {};
    // Argument is array of percentages.
    let color = reusedColor;

    color = ColorX.InvertRGB(color, filters[0] / 100);
    color = ColorX.SepiaRGB(color, filters[1] / 100);
    color = ColorX.SaturateRGB(color, filters[2] / 100);
    color = ColorX.HueRotateRGB(color, filters[3] * 3.6);
    color = ColorX.BrightnessRGB(color, filters[4] / 100);
    color = ColorX.ContrastRGB(color, filters[5] / 100);

    const reusedColorHSL = ColorX.RGB2HSL(color);
    return (
      Math.abs(color.r - colorRGBA.r) +
      Math.abs(color.g - colorRGBA.g) +
      Math.abs(color.b - colorRGBA.b) +
      Math.abs(reusedColorHSL.h - colorHSL.h) +
      Math.abs(reusedColorHSL.s - colorHSL.s) +
      Math.abs(reusedColorHSL.l - colorHSL.l)
    );
  }

  static CSS(filters) {
    return `invert(${this.Format(filters[0])}%) sepia(${this.Format(filters[1])}%) saturate(${this.Format(filters[2])}%) hue-rotate(${this.Format(filters[3], 3.6)}deg) brightness(${this.Format(filters[4])}%) contrast(${this.Format(filters[5])}%)`;
  }

  static Format(value, multiplier = 1) {
    return Math.round(value * multiplier);
  }
}

export default ColorXAddOns;